/**
 * apiBase — single source of truth for the backend API host.
 *
 * Resolves in this order:
 *   1. process.env.REACT_APP_API_URL (newer convention — some files use this)
 *   2. process.env.REACT_APP_BACKEND_URL (older convention — most files use this)
 *   3. PROD_FALLBACK — a hardcoded production host so a fresh Railway
 *      deploy without env vars still reaches the backend.
 *
 * Why the hardcoded fallback: CRA bakes process.env.REACT_APP_* into the
 * static bundle at BUILD time. The Railway frontend service didn't set
 * the env var (and the .env file is gitignored / not deployed), so the
 * bundled code had `process.env.REACT_APP_BACKEND_URL = undefined`,
 * which made every fetch a same-origin relative call. That hit the FE's
 * SPA fallback (index.html), the FE then JSON.parsed `<!doctype...`,
 * and threw "Unexpected token '<'". Hardcoding the prod host here means
 * the bug can't recur from a missed env var in any future deploy.
 *
 * Override: set REACT_APP_API_URL or REACT_APP_BACKEND_URL in the
 * Railway build environment for non-prod hosts (preview, dev, etc.).
 */

const PROD_FALLBACK = 'https://pccapp-production.up.railway.app';

const _stripTrailingSlash = (s) => (typeof s === 'string' ? s.replace(/\/+$/, '') : s);

// The frontend and the API are SEPARATE Railway services (the web host serves
// the SPA only; /api lives on the backend host). So the API base must be an
// ABSOLUTE host that actually serves /api — never the page's own origin (a
// same-origin/relative base would hit the SPA fallback (index.html) and every
// fetch would JSON.parse "<!doctype…").
//
// Custom domain (2026-09): the app is served from app.pariscentrechretien.com
// and the API from api.pariscentrechretien.com. Pointing at the same-SITE api.
// host keeps the SameSite=Lax auth cookie flowing (both under
// pariscentrechretien.com). Every OTHER host — the legacy Railway web domain
// and the Capacitor native shell (capacitor://localhost) — keeps the absolute
// Railway backend, so nothing that worked before breaks.
const _resolve = () => {
  const envUrl =
    _stripTrailingSlash(process.env.REACT_APP_API_URL) ||
    _stripTrailingSlash(process.env.REACT_APP_BACKEND_URL);
  if (envUrl) return envUrl;
  try {
    if (typeof window !== 'undefined' && window.location) {
      const host = window.location.hostname || '';
      if (host === 'pariscentrechretien.com' || host.endsWith('.pariscentrechretien.com')) {
        return 'https://api.pariscentrechretien.com';
      }
    }
  } catch (_) {
    /* fall through to the production Railway backend */
  }
  return PROD_FALLBACK;
};

export const API_BASE = _resolve();

// Default export so callers can `import API_BASE from '../utils/apiBase'`.
export default API_BASE;
